<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const subject = ref('');
const inboxId = ref(null);
const priority = ref('');
const assigneeId = ref(null);
const selectedLabels = ref([]);
const internalNote = ref('');
const includeTranscript = ref(true);
const isCreating = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const currentUserId = computed(() => store.getters.getCurrentUserID);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const agents = computed(() => store.getters['agents/getVerifiedAgents']);
const labels = computed(() => store.getters['labels/getLabels']);

const contact = computed(() =>
  store.getters['contacts/getContact'](currentChat.value?.meta?.sender?.id)
);

const customerName = computed(
  () => contact.value?.name || currentChat.value?.meta?.sender?.name || ''
);
const customerEmail = computed(
  () => contact.value?.email || currentChat.value?.meta?.sender?.email || ''
);

const inboxOptions = computed(() =>
  inboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);

const priorityOptions = computed(() => [
  { value: '', label: t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY.NONE') },
  { value: 'low', label: t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY.LOW') },
  {
    value: 'medium',
    label: t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY.MEDIUM'),
  },
  { value: 'high', label: t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY.HIGH') },
  {
    value: 'urgent',
    label: t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY.URGENT'),
  },
]);

const agentOptions = computed(() =>
  agents.value.map(agent => ({ value: agent.id, label: agent.name }))
);

const labelOptions = computed(() =>
  labels.value.map(label => ({ value: label.title, label: label.title }))
);

const selectedInbox = computed(() =>
  inboxes.value.find(inbox => inbox.id === inboxId.value)
);

// Contact email is only a requirement for email inboxes; any other channel
// generates its own source id (or fails server-side with a clear error).
const needsContactEmail = computed(
  () =>
    selectedInbox.value?.channel_type === INBOX_TYPES.EMAIL &&
    !customerEmail.value
);

const isSubmitDisabled = computed(
  () => !subject.value.trim() || !inboxId.value || needsContactEmail.value
);

const open = () => {
  subject.value =
    currentChat.value?.additional_attributes?.mail_subject ||
    t('TICKETS.CREATE_FROM_CONVERSATION.SUBJECT_FALLBACK', {
      displayId: currentChat.value?.id,
    });
  inboxId.value = currentChat.value?.inbox_id || inboxes.value[0]?.id || null;
  priority.value = '';
  assigneeId.value = currentUserId.value;
  selectedLabels.value = [];
  internalNote.value = '';
  includeTranscript.value = Boolean(customerEmail.value);
  store.dispatch('agents/get');
  store.dispatch('labels/get');
  dialogRef.value.open();
};

const onConfirm = async () => {
  if (isSubmitDisabled.value || isCreating.value) return;
  isCreating.value = true;
  try {
    const data = await store.dispatch('createLinkedTicket', {
      conversationId: currentChat.value.id,
      inboxId: inboxId.value,
      subject: subject.value.trim(),
      priority: priority.value || null,
      assigneeId: assigneeId.value,
      labels: selectedLabels.value,
      internalNote: internalNote.value.trim() || null,
      includeTranscript:
        includeTranscript.value && Boolean(customerEmail.value),
    });
    dialogRef.value.close();
    useAlert(t('TICKETS.CREATE_FROM_CONVERSATION.SUCCESS'), {
      type: 'link',
      to: `/app/accounts/${accountId.value}/tickets/${data.id}`,
      message: t('TICKETS.CREATE_FROM_CONVERSATION.GO_TO_TICKET'),
    });
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('TICKETS.CREATE_FROM_CONVERSATION.ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('TICKETS.CREATE_FROM_CONVERSATION.TITLE')"
    :confirm-button-label="t('TICKETS.CREATE_FROM_CONVERSATION.SUBMIT')"
    :disable-confirm-button="isSubmitDisabled"
    :is-loading="isCreating"
    overflow-y-auto
    @confirm="onConfirm"
  >
    <div class="flex flex-col gap-4">
      <div
        class="flex flex-col gap-1.5 p-3 rounded-lg bg-n-alpha-1"
        data-testid="linked-ticket-customer"
      >
        <div class="flex items-center justify-between gap-2 text-sm">
          <span class="text-n-slate-11">
            {{ t('TICKETS.CREATE_FROM_CONVERSATION.CUSTOMER_NAME') }}
          </span>
          <span class="truncate text-n-slate-12">{{ customerName }}</span>
        </div>
        <div class="flex items-center justify-between gap-2 text-sm">
          <span class="text-n-slate-11">
            {{ t('TICKETS.CREATE_FROM_CONVERSATION.CUSTOMER_EMAIL') }}
          </span>
          <span
            class="truncate"
            :class="customerEmail ? 'text-n-slate-12' : 'text-n-slate-10'"
            data-testid="linked-ticket-customer-email"
          >
            {{
              customerEmail || t('TICKETS.CREATE_FROM_CONVERSATION.NO_EMAIL')
            }}
          </span>
        </div>
      </div>

      <Input
        v-model="subject"
        data-testid="linked-ticket-subject"
        :label="t('TICKETS.CREATE_FROM_CONVERSATION.SUBJECT_LABEL')"
        :placeholder="t('TICKETS.CREATE_FROM_CONVERSATION.SUBJECT_PLACEHOLDER')"
        autofocus
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.CREATE_FROM_CONVERSATION.INBOX_LABEL') }}
        </label>
        <Select
          v-model="inboxId"
          data-testid="linked-ticket-inbox"
          :options="inboxOptions"
        />
        <p
          v-if="needsContactEmail"
          class="m-0 text-xs text-n-ruby-9"
          data-testid="linked-ticket-no-email-hint"
        >
          {{ t('TICKETS.CREATE_FROM_CONVERSATION.NO_CONTACT_EMAIL') }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TICKETS.CREATE_FROM_CONVERSATION.PRIORITY_LABEL') }}
          </label>
          <Select
            v-model="priority"
            data-testid="linked-ticket-priority"
            :options="priorityOptions"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TICKETS.CREATE_FROM_CONVERSATION.ASSIGNEE_LABEL') }}
          </label>
          <Select
            v-model="assigneeId"
            data-testid="linked-ticket-assignee"
            :options="agentOptions"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('TICKETS.CREATE_FROM_CONVERSATION.TAGS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="selectedLabels"
          :options="labelOptions"
          :placeholder="t('TICKETS.CREATE_FROM_CONVERSATION.TAGS_PLACEHOLDER')"
        />
      </div>

      <TextArea
        v-model="internalNote"
        data-testid="linked-ticket-note"
        :label="t('TICKETS.CREATE_FROM_CONVERSATION.NOTE_LABEL')"
        :placeholder="t('TICKETS.CREATE_FROM_CONVERSATION.NOTE_PLACEHOLDER')"
        :max-length="2000"
      />

      <div class="flex flex-col gap-1">
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <Checkbox
            v-model="includeTranscript"
            data-testid="linked-ticket-transcript"
            :disabled="!customerEmail"
          />
          {{ t('TICKETS.CREATE_FROM_CONVERSATION.TRANSCRIPT_LABEL') }}
        </label>
        <p
          v-if="!customerEmail"
          class="m-0 text-xs text-n-slate-10"
          data-testid="linked-ticket-transcript-hint"
        >
          {{ t('TICKETS.CREATE_FROM_CONVERSATION.TRANSCRIPT_NO_EMAIL') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
